import { useEffect, useRef, useState } from "react";
import { useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import type { ShellNavOutput } from "@/api/tools";
import { SHELL_LABELS } from "@/mc/g4a/labels";
import { navTitle, useShellNav } from "./nav";
import { sectionWords } from "./sectionTitle";

/** A tab's title: the screen (and its section), then the BRD title the lockup carries (SH-11). */
export function documentTitle(screen: string): string {
  return `${screen} · ${SHELL_LABELS.brandTitle}`;
}

/**
 * A new screen's arrival: its heading takes focus without scrolling, and the main scroller starts at the top, so what
 * sits above the heading (Record what already happened, Change openings) is never clipped under the top bar (F-82).
 */
export function arriveAt(heading: HTMLElement, scroller: HTMLElement | null): void {
  heading.tabIndex = -1;
  heading.focus({ preventScroll: true });
  if (scroller) scroller.scrollTop = 0;
}

/** B1's own screens, named before the navigation has loaded (and Settings, which the nav lists for every seat). */
const STATIC_TITLES: Readonly<Record<string, string>> = { home: "Home", "action-center": "Action Center", settings: "Settings" };

/**
 * One route owner waits for the actual screen, including asynchronous object reads. A heading may carry its locator in
 * `data-title` when its words are not the screen's name (Home's heading is the greeting; the title stays "Home").
 */
export function RouteLifecycle() {
  const route = useRoute();
  const { me, loading } = useSession();
  const items = useShellNav().data;
  const [announcement, announce] = useState("");
  const signedIn = !!me;
  const screenKey = `${route.view}/${route.parts.join("/")}`;
  const hash = route.raw;
  const navigation = useRef(items);
  navigation.current = items;
  useEffect(() => {
    if (loading) return;
    announce("");
    document.title = documentTitle(signedIn ? titleFor(hash, navigation.current) : "Sign in");
    let frame = 0;
    let arrived = false;
    let spoken = "";
    const observer = new MutationObserver(() => { cancelAnimationFrame(frame); frame = requestAnimationFrame(arrive); });
    function arrive() {
      const heading = Array.from(document.querySelectorAll<HTMLElement>("main h1")).find(node => node.getClientRects().length > 0);
      if (!heading) return; // The read guard still owns its loading skeleton.
      const section = sectionWords(document);
      const words = heading.dataset.title?.trim() || heading.textContent?.trim();
      const screen = !signedIn ? "Sign in" : words === "We can't show that page" ? "Not found"
        : [words || titleFor(hash, navigation.current), section].filter(Boolean).join(" · ");
      document.title = documentTitle(screen);
      // Late object reads may change the title. Arrival focus belongs only to a
      // new screen, never to filtering, paging or an updated heading on this one.
      // The announcement names what the title names (§6.2): a section that comes
      // with a late read — Interview Guidance's level tabs — is spoken once it is
      // there, without moving focus again.
      if (arrived) {
        if (signedIn && screen !== spoken) { spoken = screen; announce(screen); }
        return;
      }
      arrived = true;
      if (signedIn && !document.querySelector('[role="dialog"]')) arriveAt(heading, document.getElementById("main"));
      // Sign-in keeps §2's first-field focus. Its title/arrival use the same owner.
      spoken = screen;
      announce(screen);
    }
    observer.observe(document.getElementById("root")!, { childList: true, subtree: true, characterData: true });
    frame = requestAnimationFrame(arrive);
    return () => { cancelAnimationFrame(frame); observer.disconnect(); };
  }, [screenKey, route.view, signedIn, loading]);
  return <div className="sr-only" role="status" aria-live="polite" aria-atomic="true" data-route-announcer>{announcement}</div>;
}

function titleFor(hash: string, nav: ShellNavOutput | undefined): string {
  const view = hash.split(/[/?]/)[0] ?? "";
  return navTitle(nav, hash) ?? STATIC_TITLES[view] ?? "Not found";
}
